import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search, Phone, ChevronDown, User, LayoutDashboard, Settings, HelpCircle, LogOut } from 'lucide-react';
import { NAV_ITEMS } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import UserProfileDropdown from './UserProfileDropdown';

const Header: React.FC = () => {
  const location = useLocation();
  const isDashboard = location.pathname.startsWith('/student') ||
    location.pathname.startsWith('/admin/dashboard') ||
    location.pathname.startsWith('/admin/student');

  if (isDashboard) return null;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { isLoggedIn, role, name, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const dashboardLink = role === 'admin' ? '/admin/dashboard' : '/student/dashboard';

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 font-sans ${scrolled ? 'shadow-xl' : ''}`}>

      {/* Top Bar */}
      <div className="bg-[#00151a] text-slate-400 text-[10px] md:text-xs py-2 px-6 md:px-8 border-b border-[#011E26]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center space-x-6">
            <a href="tel:+447577436898" className="flex items-center gap-1 hover:text-[#009B8F] cursor-pointer transition-colors">
              <Phone size={12} /> <span className="hidden sm:inline">Call:</span> +44 757 743 6898
            </a>
            <a href="https://wa.me/447577436898" target="_blank" rel="noopener noreferrer" className="hidden sm:flex items-center gap-1 hover:text-[#009B8F] cursor-pointer transition-colors">
              Whatsapp: +44 757 743 6898
            </a>
            <a href="mailto:info@nexoverseas.co.uk" className="hidden lg:inline hover:text-[#009B8F] cursor-pointer transition-colors">
              info@nexoverseas.co.uk
            </a>
          </div>
          <div className="flex items-center space-x-6 uppercase tracking-widest font-bold">
            <span className="hover:text-[#009B8F] cursor-pointer transition-colors hidden sm:inline" onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}>Contact Us</span>
            {!isLoggedIn && (
              <Link to="/login" className="hover:text-[#009B8F] cursor-pointer transition-colors flex items-center gap-1">
                <User size={12} /> Login
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <div className={`transition-all duration-300 ${scrolled ? 'bg-[#011E26] py-3' : 'bg-[#011E26]/90 backdrop-blur-sm py-5'}`}>
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex justify-between items-center">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <img
              src="/assets/logo.png"
              alt="NexOverseas Logo"
              className="h-14 w-auto object-contain rounded-xl"
            />
          </Link>

          {/* Desktop Nav with Dropdowns */}
          <nav className="hidden lg:flex items-center space-x-1">
            {NAV_ITEMS.map((item) => (
              <div key={item.label} className="group relative">
                <a
                  href={item.href}
                  className="flex items-center gap-1 text-white text-xs font-bold tracking-widest px-4 py-3 hover:text-[#009B8F] transition-colors uppercase"
                >
                  {item.label}
                  {item.subItems && <ChevronDown size={12} className="group-hover:rotate-180 transition-transform duration-300" />}
                </a>

                {/* Dropdown Menu */}
                {item.subItems && (
                  <div className="absolute top-full left-0 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                    <div className="bg-white border-t-4 border-[#009B8F] shadow-2xl w-56 rounded-b-sm py-2">
                      {item.subItems.map(sub => (
                        <a
                          key={sub.label}
                          href={sub.link}
                          className="block px-4 py-2 text-xs font-bold text-slate-700 hover:bg-gray-50 hover:text-[#009B8F] tracking-wide uppercase border-b border-gray-50 last:border-0"
                        >
                          {sub.label}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Actions */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/eligibility" className="text-white hover:text-[#009B8F] transition-colors p-2" aria-label="Search services">
              <Search size={18} />
            </Link>
            {isLoggedIn && <UserProfileDropdown />}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-white hover:text-[#009B8F]"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-[#011E26] border-t border-[#009B8F] shadow-xl z-50 max-h-[85vh] overflow-y-auto">
          <div className="flex flex-col p-6 space-y-4">
            {/* Logged In User Mobile Links */}
            {isLoggedIn && (
              <div className="border-b border-slate-700 pb-4 mb-2">
                <div className="flex items-center gap-3 mb-4 text-white">
                  <div className="w-10 h-10 rounded-full bg-[#009B8F]/20 flex items-center justify-center text-[#009B8F] font-bold border border-[#009B8F]">
                    {name?.charAt(0).toUpperCase() || 'S'}
                  </div>
                  <div>
                    <p className="font-bold">{name || 'Student'}</p>
                    <p className="text-xs text-slate-400 capitalize">{role || 'client'}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Link to={dashboardLink} onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#009B8F] py-2">
                    <LayoutDashboard size={16} /> Dashboard
                  </Link>
                  <Link to="/student/profile" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#009B8F] py-2">
                    <User size={16} /> My Profile
                  </Link>
                  <Link to="/settings" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 text-sm text-slate-300 hover:text-[#009B8F] py-2">
                    <Settings size={16} /> Account Settings
                  </Link>
                </div>
              </div>
            )}

            {NAV_ITEMS.map((item) => (
              <div key={item.label} className="border-b border-slate-800 pb-2">
                <a
                  href={item.href}
                  className="block text-sm font-bold tracking-widest text-white uppercase mb-2"
                  onClick={() => !item.subItems && setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
                {item.subItems && (
                  <div className="pl-4 space-y-2 border-l border-[#009B8F]/30 ml-1">
                    {item.subItems.map(sub => (
                      <a
                        key={sub.label}
                        href={sub.link}
                        className="block text-xs text-slate-400 hover:text-[#009B8F] py-1"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {sub.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-4 flex flex-col gap-3">
              {!isLoggedIn ? (
                <Link to="/login" onClick={() => setIsMobileMenuOpen(false)} className="w-full py-3 text-center bg-[#009B8F] text-white text-xs font-bold uppercase tracking-widest rounded-lg">
                  Client Login / Sign Up
                </Link>
              ) : (
                <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="w-full py-3 text-center bg-red-500 hover:bg-red-600 text-white text-xs font-bold uppercase tracking-widest rounded-lg flex items-center justify-center gap-2">
                  <LogOut size={14} /> Logout
                </button>
              )}

              <Link to="/eligibility" onClick={() => setIsMobileMenuOpen(false)} className="w-full py-3 text-center border border-slate-600 text-slate-300 text-xs font-bold uppercase tracking-widest rounded-lg block">
                Start Your Journey
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;